// Hoofdkaart-component met MapLibre GL JS:
//  - PDOK Luchtfoto Actueel HR als raster-laag (WMTS, EPSG:3857).
//  - Blokken als GeoJSON FeatureCollection met fill + line + selectie-layers.
//  - Alle teken-interacties (polygoon/rechthoek/vertex slepen/blok slepen/
//    delete) afgehandeld via map-events; mode komt uit de Zustand-store.
//  - Snap-to-vertex op 12 px afstand.
//
// We bewaren alle hits/feedback-rendering in twee aparte GeoJSON-sources:
// "blocks" voor de echte blokken en "draw" voor de live tekenpreview.

import { useEffect, useRef } from "react";
import maplibregl, {
  type Map as MlMap,
  type StyleSpecification,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { REFERENCE_YEAR, useStore } from "../state/store";
import type { Block } from "../types/scenario";
import { blocksBounds, findSnapVertex, findVertexIndex, pointInPolygon } from "../utils/geo";
import {
  computeFlatBlockShadows,
  computeRoofShadows,
  computeShadows,
  dayOfYearToMonthDay,
  getSunPosition,
  nlClockToUtc,
  type SurfaceResult,
} from "../shadow";

const PDOK_LUCHTFOTO_URL =
  "https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/Actueel_orthoHR/EPSG:3857/{z}/{x}/{y}.jpeg";

const PDOK_BRT_URL =
  "https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png";

const PDOK_ATTRIBUTION =
  '&copy; <a href="https://www.beeldmateriaal.nl" target="_blank" rel="noopener">Beeldmateriaal Nederland</a> / <a href="https://www.pdok.nl" target="_blank" rel="noopener">PDOK</a>';

const PDOK_BRT_ATTRIBUTION =
  '&copy; <a href="https://www.kadaster.nl" target="_blank" rel="noopener">Kadaster</a> / <a href="https://www.pdok.nl" target="_blank" rel="noopener">PDOK</a> (BRT-Achtergrondkaart)';

const SNAP_PIXELS = 12;

// IDs van alle MapLibre-fill-layers die de gebruiker-configureerbare schaduw-
// doorzichtigheid gebruiken. Bij elke wijziging van shadowOpacity loopt de
// effect-handler hier overheen om setPaintProperty te draaien.
const SHADOW_LAYER_IDS = [
  "shadows-fill",
  "shadows-fill-roofs",
  "shadows-fill-flats",
] as const;

/**
 * Bouw de fill-opacity-expressie voor de schaduwlagen.
 * `factor` is de gebruikersinstelling als breuk (0–1); blockingFactor uit de
 * feature wordt erbij vermenigvuldigd zodat doorlatende blokken (boombladdek)
 * lichter schaduwen blijven werpen.
 */
function shadowOpacityExpression(factor: number): maplibregl.DataDrivenPropertyValueSpecification<number> {
  return ["*", ["coalesce", ["get", "blockingFactor"], 1], factor];
}

const baseStyle: StyleSpecification = {
  version: 8,
  // MapLibre vereist een glyphs-URL voor tekstrendering; wij gebruiken
  // (nog) geen tekstlabels op de kaart, dus laten we het weg en omzeilen
  // de waarschuwing door geen label-layers toe te voegen.
  sources: {
    pdok: {
      type: "raster",
      tiles: [PDOK_LUCHTFOTO_URL],
      tileSize: 256,
      minzoom: 0,
      maxzoom: 19,
      attribution: PDOK_ATTRIBUTION,
    },
    "pdok-brt": {
      type: "raster",
      tiles: [PDOK_BRT_URL],
      tileSize: 256,
      minzoom: 0,
      maxzoom: 19,
      attribution: PDOK_BRT_ATTRIBUTION,
    },
  },
  layers: [
    {
      id: "pdok-luchtfoto",
      type: "raster",
      source: "pdok",
    },
    {
      id: "pdok-brt-laag",
      type: "raster",
      source: "pdok-brt",
      // Standaard verborgen; wordt aan/uit getoggled vanuit syncBasemap.
      layout: { visibility: "none" },
    },
  ],
};

function blocksToGeoJSON(blocks: Block[], selectedId: string | null): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: blocks.map((b) => ({
      type: "Feature",
      id: b.id,
      properties: {
        id: b.id,
        name: b.name,
        type: b.type,
        color: b.color,
        opacity: (b.opacity ?? 60) / 100,
        selected: b.id === selectedId ? 1 : 0,
        heightTop: b.heightTop,
      },
      geometry: {
        type: "Polygon",
        coordinates: [closeRing(b.polygon)],
      },
    })),
  };
}

function closeRing(ring: [number, number][]): [number, number][] {
  if (ring.length < 1) return ring;
  const [lng0, lat0] = ring[0];
  const last = ring[ring.length - 1];
  if (last[0] === lng0 && last[1] === lat0) return ring;
  return [...ring, [lng0, lat0]];
}

function drawingToGeoJSON(
  points: [number, number][],
  cursor: [number, number] | null,
  rectangleAnchor: [number, number] | null,
  mode: string,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  if (mode === "draw-polygon" && points.length > 0) {
    // Lijn met cursor als laatste punt.
    const line: [number, number][] = cursor ? [...points, cursor] : points;
    features.push({
      type: "Feature",
      properties: { kind: "preview-line" },
      geometry: { type: "LineString", coordinates: line },
    });
    for (const p of points) {
      features.push({
        type: "Feature",
        properties: { kind: "preview-vertex" },
        geometry: { type: "Point", coordinates: p },
      });
    }
  }
  if (mode === "draw-rectangle" && rectangleAnchor && cursor) {
    const ring: [number, number][] = [
      [rectangleAnchor[0], rectangleAnchor[1]],
      [cursor[0], rectangleAnchor[1]],
      [cursor[0], cursor[1]],
      [rectangleAnchor[0], cursor[1]],
      [rectangleAnchor[0], rectangleAnchor[1]],
    ];
    features.push({
      type: "Feature",
      properties: { kind: "preview-rect" },
      geometry: { type: "Polygon", coordinates: [ring] },
    });
  }
  return { type: "FeatureCollection", features };
}

export default function Map2D() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);

  // Verslepen van vertex/blok wordt buiten React-renders bijgehouden om
  // re-renders per muisbeweging te vermijden. We lezen de relevante state
  // bij start, muteren via store-actions, en resetten bij mouseup.
  const dragState = useRef<
    | { kind: "vertex"; blockId: string; vertexIndex: number; original: [number, number][] }
    | { kind: "block"; blockId: string; startLngLat: [number, number] }
    | null
  >(null);

  // Mount: maak de kaart aan.
  useEffect(() => {
    if (!containerRef.current) return;
    const view = useStore.getState().scenario.view;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle,
      center: [view.centerLng, view.centerLat],
      zoom: view.zoom,
      bearing: view.rotation,
      attributionControl: { compact: true },
      // preserveDrawingBuffer is nodig om via canvas.toDataURL() een
      // snapshot te kunnen maken; kost extra GPU-geheugen maar is op
      // onze schaal verwaarloosbaar.
      preserveDrawingBuffer: true,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");
    mapRef.current = map;
    mapInstance = map;

    map.on("load", () => {
      // Schaduw-laag: onder de blokken, boven de luchtfoto. Donker
      // semi-transparant; dichtheid hangt af van blockingFactor (transparante
      // blokken werpen lichtere schaduw).
      map.addSource("shadows", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "shadows-fill",
        type: "fill",
        source: "shadows",
        paint: {
          "fill-color": "#0b1220",
          "fill-opacity": shadowOpacityExpression(
            useStore.getState().shadowOpacity / 100,
          ),
          "fill-antialias": true,
        },
      });

      map.addSource("heatmap", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addSource("blocks", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "blocks-fill",
        type: "fill",
        source: "blocks",
        paint: {
          "fill-color": ["coalesce", ["get", "color"], "#888888"],
          "fill-opacity": ["get", "opacity"],
        },
      });
      // Schaduwen op dakvlakken (z > 0): geprojecteerd tegen de werkelijke
      // hoogte van het bovenvlak van elk blok en geclipt op de footprint.
      // Wordt bóven blocks-fill getekend zodat de schaduw op het dak zichtbaar
      // is; onder de heatmap zodat de aggregaat-kleuren niet gedimd worden.
      map.addSource("shadow-roofs", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "shadows-fill-roofs",
        type: "fill",
        source: "shadow-roofs",
        paint: {
          "fill-color": "#0b1220",
          "fill-opacity": shadowOpacityExpression(
            useStore.getState().shadowOpacity / 100,
          ),
          "fill-antialias": true,
        },
      });
      // Schaduwen op platte ontvangende blokken (heightTop = 0): tuin,
      // bestrating, plat gebouw. Zonder deze laag dimt blocks-fill de
      // grondschaduw die over de footprint heen loopt — als de heatmap
      // uit staat moet je de schaduw juist duidelijk kunnen zien.
      // Onder heatmap-cells zodat de aggregaat-kleuren prioriteit houden
      // wanneer de heatmap aan staat.
      map.addSource("shadow-flats", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "shadows-fill-flats",
        type: "fill",
        source: "shadow-flats",
        paint: {
          "fill-color": "#0b1220",
          "fill-opacity": shadowOpacityExpression(
            useStore.getState().shadowOpacity / 100,
          ),
          "fill-antialias": true,
        },
      });
      // Heatmap boven blocks-fill (en boven de roof-shadow) maar onder
      // blocks-outline: zo dekt het celraster de bloklkeur op meetvlakken
      // af, terwijl de bloklijnen de contour blijven aangeven.
      map.addLayer({
        id: "heatmap-cells",
        type: "circle",
        source: "heatmap",
        paint: {
          // Cell-radius schaalt mee met zoom — bij z=18 ongeveer 0.5 m breed.
          "circle-radius": [
            "interpolate",
            ["exponential", 2],
            ["zoom"],
            14,
            1,
            18,
            6,
            22,
            36,
          ],
          "circle-color": [
            "interpolate",
            ["linear"],
            ["get", "shadow"],
            0,
            "#2c7bb6",
            0.25,
            "#abd9e9",
            0.5,
            "#ffffbf",
            0.75,
            "#fdae61",
            1,
            "#d7191c",
          ],
          "circle-opacity": 0.95,
          "circle-stroke-width": 0,
        },
      });
      map.addLayer({
        id: "blocks-outline",
        type: "line",
        source: "blocks",
        paint: {
          "line-color": [
            "case",
            ["==", ["get", "selected"], 1],
            "#1d4ed8",
            "#1f2937",
          ],
          "line-width": ["case", ["==", ["get", "selected"], 1], 3, 1.5],
        },
      });
      // Vertex-handles voor het geselecteerde blok.
      map.addSource("selected-vertices", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "selected-vertices",
        type: "circle",
        source: "selected-vertices",
        paint: {
          "circle-radius": 5,
          "circle-color": "#ffffff",
          "circle-stroke-color": "#1d4ed8",
          "circle-stroke-width": 2,
        },
      });

      map.addSource("draw", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "draw-rect",
        type: "fill",
        source: "draw",
        filter: ["==", ["get", "kind"], "preview-rect"],
        paint: { "fill-color": "#1d4ed8", "fill-opacity": 0.2 },
      });
      map.addLayer({
        id: "draw-rect-outline",
        type: "line",
        source: "draw",
        filter: ["==", ["get", "kind"], "preview-rect"],
        paint: { "line-color": "#1d4ed8", "line-width": 2, "line-dasharray": [2, 2] },
      });
      map.addLayer({
        id: "draw-line",
        type: "line",
        source: "draw",
        filter: ["==", ["get", "kind"], "preview-line"],
        paint: { "line-color": "#1d4ed8", "line-width": 2, "line-dasharray": [2, 2] },
      });
      map.addLayer({
        id: "draw-vertex",
        type: "circle",
        source: "draw",
        filter: ["==", ["get", "kind"], "preview-vertex"],
        paint: {
          "circle-radius": 4,
          "circle-color": "#1d4ed8",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      });

      // Initiële render uit de store.
      syncBlocks();
      syncDrawing();
      syncShadows();
      syncHeatmap();
      syncBasemap();
      syncCursorStyle();
    });

    // Verschuif kaart-view → store, zodat scenario-export het opslaat.
    map.on("moveend", () => {
      const c = map.getCenter();
      useStore.getState().setScenarioView({
        centerLat: c.lat,
        centerLng: c.lng,
        zoom: map.getZoom(),
        rotation: map.getBearing(),
      });
    });

    // Pointer-handlers — gedelegeerd op basis van de huidige drawMode.
    map.on("click", handleClick);
    map.on("dblclick", handleDblClick);
    map.on("contextmenu", handleContextMenu);
    map.on("mousemove", handleMouseMove);
    map.on("mousedown", handleMouseDown);
    map.on("mouseup", handleMouseUp);

    return () => {
      map.remove();
      mapRef.current = null;
      mapInstance = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Houd de kaart-data gesynchroniseerd met de store.
  useEffect(() => {
    const unsub = useStore.subscribe((s, prev) => {
      if (s.scenario.blocks !== prev.scenario.blocks || s.selectedBlockId !== prev.selectedBlockId) {
        syncBlocks();
      }
      if (
        s.scenario.blocks !== prev.scenario.blocks ||
        s.currentDayOfYear !== prev.currentDayOfYear ||
        s.currentMinuteOfDay !== prev.currentMinuteOfDay ||
        s.scenario.view.centerLat !== prev.scenario.view.centerLat ||
        s.scenario.view.centerLng !== prev.scenario.view.centerLng
      ) {
        syncShadows();
      }
      if (
        s.drawing !== prev.drawing ||
        s.drawMode !== prev.drawMode
      ) {
        syncDrawing();
        syncCursorStyle();
      }
      if (
        s.yearResult !== prev.yearResult ||
        s.heatmapVisible !== prev.heatmapVisible
      ) {
        syncHeatmap();
      }
      if (s.basemap !== prev.basemap) {
        syncBasemap();
      }
      if (s.shadowOpacity !== prev.shadowOpacity) {
        syncShadowOpacity();
      }
      if (s.scenario.id !== prev.scenario.id) {
        // Ander scenario geladen — verplaats de kaart.
        flyToScenario();
      }
    });
    return unsub;
  }, []);

  // Pas de fill-opacity van alle schaduwlagen aan op de gebruikersvoorkeur.
  function syncShadowOpacity(): void {
    const map = mapRef.current;
    if (!map) return;
    const expr = shadowOpacityExpression(useStore.getState().shadowOpacity / 100);
    for (const layerId of SHADOW_LAYER_IDS) {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, "fill-opacity", expr);
      }
    }
  }

  /** Pas de kaart aan op het zojuist geladen scenario. Als er blokken zijn,
   * zoom-fit op hun bounding box; anders gebruik scenario.view. */
  function flyToScenario(): void {
    const map = mapRef.current;
    if (!map) return;
    const { scenario } = useStore.getState();
    const bb = blocksBounds(scenario.blocks);
    if (bb) {
      const [west, south, east, north] = bb;
      map.fitBounds(
        [
          [west, south],
          [east, north],
        ],
        { padding: 60, maxZoom: 19, duration: 600 },
      );
    } else {
      map.flyTo({
        center: [scenario.view.centerLng, scenario.view.centerLat],
        zoom: scenario.view.zoom,
        bearing: scenario.view.rotation,
        duration: 600,
      });
    }
  }

  function syncBlocks(): void {
    const map = mapRef.current;
    if (!map || !map.getSource("blocks")) return;
    const { scenario, selectedBlockId } = useStore.getState();
    const data = blocksToGeoJSON(scenario.blocks, selectedBlockId);
    (map.getSource("blocks") as maplibregl.GeoJSONSource).setData(data);

    const selected = scenario.blocks.find((b) => b.id === selectedBlockId);
    const verticesData: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: selected
        ? selected.polygon.map((p, i) => ({
            type: "Feature",
            properties: { idx: i, blockId: selected.id },
            geometry: { type: "Point", coordinates: p },
          }))
        : [],
    };
    (map.getSource("selected-vertices") as maplibregl.GeoJSONSource).setData(verticesData);
  }

  function syncDrawing(): void {
    const map = mapRef.current;
    if (!map || !map.getSource("draw")) return;
    const { drawing, drawMode } = useStore.getState();
    const data = drawingToGeoJSON(
      drawing.points,
      drawing.cursor,
      drawing.rectangleAnchor,
      drawMode,
    );
    (map.getSource("draw") as maplibregl.GeoJSONSource).setData(data);
  }

  function syncHeatmap(): void {
    const map = mapRef.current;
    if (!map || !map.getSource("heatmap")) return;
    const { yearResult, heatmapVisible } = useStore.getState();
    if (!heatmapVisible || !yearResult) {
      (map.getSource("heatmap") as maplibregl.GeoJSONSource).setData({
        type: "FeatureCollection",
        features: [],
      });
      return;
    }
    const features: GeoJSON.Feature[] = [];
    for (const surface of yearResult.surfaces as SurfaceResult[]) {
      for (const cell of surface.cells) {
        features.push({
          type: "Feature",
          properties: {
            shadow: cell.shadowFraction,
            surfaceId: surface.surfaceId,
          },
          geometry: { type: "Point", coordinates: cell.lngLat },
        });
      }
    }
    (map.getSource("heatmap") as maplibregl.GeoJSONSource).setData({
      type: "FeatureCollection",
      features,
    });
  }

  function syncShadows(): void {
    const map = mapRef.current;
    if (!map || !map.getSource("shadows")) return;
    const { scenario, currentDayOfYear, currentMinuteOfDay } = useStore.getState();
    const { month, day } = dayOfYearToMonthDay(REFERENCE_YEAR, currentDayOfYear);
    const hour = Math.floor(currentMinuteOfDay / 60);
    const minute = currentMinuteOfDay % 60;
    const utc = nlClockToUtc(REFERENCE_YEAR, month, day, hour, minute);
    const sun = getSunPosition(utc, scenario.view.centerLat, scenario.view.centerLng);

    const shadows = computeShadows(scenario.blocks, sun);
    const groundData: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: shadows.map((s) => ({
        type: "Feature",
        properties: { blockId: s.blockId, blockingFactor: s.blockingFactor },
        geometry: { type: "Polygon", coordinates: [closeRing(s.polygon)] },
      })),
    };
    (map.getSource("shadows") as maplibregl.GeoJSONSource).setData(groundData);

    // Dakvlak-schaduwen: per blok met heightTop > 0 wordt de schaduw tegen
    // de werkelijke dakhoogte geprojecteerd en op de footprint geclipt.
    // Renderen op een aparte source/laag bóven blocks-fill.
    const roofSrc = map.getSource("shadow-roofs");
    if (roofSrc) {
      const roofShadows = computeRoofShadows(scenario.blocks, sun);
      const roofData: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: roofShadows.map((s) => ({
          type: "Feature",
          properties: {
            roofBlockId: s.roofBlockId,
            castingBlockId: s.castingBlockId,
            blockingFactor: s.blockingFactor,
          },
          geometry: { type: "Polygon", coordinates: [closeRing(s.polygon)] },
        })),
      };
      (roofSrc as maplibregl.GeoJSONSource).setData(roofData);
    }

    // Schaduwen op platte ontvangende blokken (heightTop = 0): grond-
    // projectie geclipt op elk plat blok zodat de schaduw niet door de
    // semi-transparante blocks-fill gedimd wordt.
    const flatSrc = map.getSource("shadow-flats");
    if (flatSrc) {
      const flatShadows = computeFlatBlockShadows(scenario.blocks, sun);
      const flatData: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: flatShadows.map((s) => ({
          type: "Feature",
          properties: {
            flatBlockId: s.flatBlockId,
            castingBlockId: s.castingBlockId,
            blockingFactor: s.blockingFactor,
          },
          geometry: { type: "Polygon", coordinates: [closeRing(s.polygon)] },
        })),
      };
      (flatSrc as maplibregl.GeoJSONSource).setData(flatData);
    }
  }

  function syncBasemap(): void {
    const map = mapRef.current;
    if (!map) return;
    const { basemap } = useStore.getState();
    if (map.getLayer("pdok-luchtfoto")) {
      map.setLayoutProperty(
        "pdok-luchtfoto",
        "visibility",
        basemap === "sat" ? "visible" : "none",
      );
    }
    if (map.getLayer("pdok-brt-laag")) {
      map.setLayoutProperty(
        "pdok-brt-laag",
        "visibility",
        basemap === "topo" ? "visible" : "none",
      );
    }
  }

  function syncCursorStyle(): void {
    const map = mapRef.current;
    if (!map) return;
    const { drawMode } = useStore.getState();
    const canvas = map.getCanvas();
    if (drawMode === "idle") {
      canvas.style.cursor = "";
    } else if (drawMode === "delete") {
      canvas.style.cursor = "not-allowed";
    } else {
      canvas.style.cursor = "crosshair";
    }
  }

  // ---- pointer handlers --------------------------------------------------

  function handleClick(e: maplibregl.MapMouseEvent): void {
    const state = useStore.getState();
    const { drawMode } = state;
    const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];

    if (drawMode === "draw-polygon") {
      const snapped = snapForCurrentDraw(e);
      // Sluit polygoon als er geklikt wordt op het beginpunt en >=3 punten.
      if (state.drawing.points.length >= 3) {
        const map = mapRef.current!;
        const first = map.project(state.drawing.points[0]);
        const dx = first.x - e.point.x;
        const dy = first.y - e.point.y;
        if (Math.hypot(dx, dy) <= SNAP_PIXELS) {
          state.finishPolygonDraw();
          return;
        }
      }
      state.drawAddPoint(snapped ?? lngLat);
      return;
    }

    if (drawMode === "draw-rectangle") {
      if (state.drawing.rectangleAnchor === null) {
        const snapped = snapForCurrentDraw(e);
        state.drawSetRectangleAnchor(snapped ?? lngLat);
      } else {
        const snapped = snapForCurrentDraw(e);
        state.finishRectangleDraw(snapped ?? lngLat);
      }
      return;
    }

    if (drawMode === "delete") {
      const hit = findBlockAt(lngLat);
      if (hit) state.removeBlock(hit.id);
      return;
    }

    // Selectie via click op fill-laag — actief in idle-modus, en in move-modes
    // alleen als er daadwerkelijk een blok geraakt wordt (zodat een misser
    // niet de selectie wegklikt en de move-modus onbruikbaar maakt).
    const map = mapRef.current!;
    const features = map.queryRenderedFeatures(e.point, { layers: ["blocks-fill"] });
    if (features.length > 0) {
      const id = features[0].properties?.id as string | undefined;
      state.selectBlock(id ?? null);
    } else if (drawMode === "idle") {
      state.selectBlock(null);
    }
  }

  function handleDblClick(e: maplibregl.MapMouseEvent): void {
    const state = useStore.getState();
    if (state.drawMode === "draw-polygon" && state.drawing.points.length >= 3) {
      e.preventDefault();
      state.finishPolygonDraw();
    }
  }

  function handleContextMenu(e: maplibregl.MapMouseEvent): void {
    const state = useStore.getState();
    if (state.drawMode === "draw-polygon" && state.drawing.points.length > 0) {
      e.preventDefault();
      state.drawPopPoint();
    }
  }

  function handleMouseMove(e: maplibregl.MapMouseEvent): void {
    const state = useStore.getState();
    const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];

    // Vertex/blok-drag tijdens move-modes
    const drag = dragState.current;
    if (drag) {
      const map = mapRef.current!;
      if (drag.kind === "vertex") {
        // Live update van het polygoon door direct de source te muteren —
        // commit naar store gebeurt op mouseup zodat we maar één undo-snapshot
        // krijgen voor de hele drag.
        const block = state.scenario.blocks.find((b) => b.id === drag.blockId);
        if (!block) return;
        const polygon = drag.original.map((p, i) =>
          i === drag.vertexIndex ? lngLat : p,
        ) as [number, number][];
        // Tijdelijk in source — overschrijf zonder undo
        (map.getSource("blocks") as maplibregl.GeoJSONSource).setData(
          blocksToGeoJSON(
            state.scenario.blocks.map((b) => (b.id === drag.blockId ? { ...b, polygon } : b)),
            state.selectedBlockId,
          ),
        );
        (map.getSource("selected-vertices") as maplibregl.GeoJSONSource).setData({
          type: "FeatureCollection",
          features: polygon.map((p, i) => ({
            type: "Feature",
            properties: { idx: i },
            geometry: { type: "Point", coordinates: p },
          })),
        });
      } else if (drag.kind === "block") {
        const dLng = lngLat[0] - drag.startLngLat[0];
        const dLat = lngLat[1] - drag.startLngLat[1];
        (map.getSource("blocks") as maplibregl.GeoJSONSource).setData(
          blocksToGeoJSON(
            state.scenario.blocks.map((b) =>
              b.id === drag.blockId
                ? {
                    ...b,
                    polygon: b.polygon.map((p) => [p[0] + dLng, p[1] + dLat] as [number, number]),
                  }
                : b,
            ),
            state.selectedBlockId,
          ),
        );
      }
      return;
    }

    if (state.drawMode === "draw-polygon" || state.drawMode === "draw-rectangle") {
      const snapped = snapForCurrentDraw(e);
      state.drawSetCursor(snapped ?? lngLat);
    }
  }

  function handleMouseDown(e: maplibregl.MapMouseEvent): void {
    if (e.originalEvent.button !== 0) return; // alleen linkermuis
    const state = useStore.getState();
    const map = mapRef.current!;
    const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];

    if (state.drawMode === "move-vertex" && state.selectedBlockId) {
      const block = state.scenario.blocks.find((b) => b.id === state.selectedBlockId);
      if (!block) return;
      const idx = findVertexIndex(
        block,
        e.point,
        (ll) => map.project(ll),
        SNAP_PIXELS,
      );
      if (idx >= 0) {
        e.preventDefault();
        map.dragPan.disable();
        dragState.current = {
          kind: "vertex",
          blockId: block.id,
          vertexIndex: idx,
          original: block.polygon.map((p) => [...p] as [number, number]),
        };
      }
      return;
    }

    if (state.drawMode === "move-block" && state.selectedBlockId) {
      const hit = findBlockAt(lngLat);
      if (hit && hit.id === state.selectedBlockId) {
        e.preventDefault();
        map.dragPan.disable();
        dragState.current = { kind: "block", blockId: hit.id, startLngLat: lngLat };
      }
      return;
    }
  }

  function handleMouseUp(e: maplibregl.MapMouseEvent): void {
    const drag = dragState.current;
    if (!drag) return;
    const state = useStore.getState();
    const map = mapRef.current!;
    const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];

    if (drag.kind === "vertex") {
      const original = drag.original[drag.vertexIndex];
      // Sla commit + undo-snapshot over als de vertex niet bewogen is.
      if (Math.abs(lngLat[0] - original[0]) < 1e-9 && Math.abs(lngLat[1] - original[1]) < 1e-9) {
        dragState.current = null;
        map.dragPan.enable();
        syncBlocks();
        return;
      }
      const polygon = drag.original.map((p, i) =>
        i === drag.vertexIndex ? lngLat : p,
      ) as [number, number][];
      state.setBlockPolygon(drag.blockId, polygon);
    } else if (drag.kind === "block") {
      const dLng = lngLat[0] - drag.startLngLat[0];
      const dLat = lngLat[1] - drag.startLngLat[1];
      if (Math.abs(dLng) > 0 || Math.abs(dLat) > 0) {
        state.moveBlockBy(drag.blockId, dLng, dLat);
      }
    }
    dragState.current = null;
    map.dragPan.enable();
    // Forceer een resync zodat de live (drag) source wordt vervangen door
    // de officiële store-state.
    syncBlocks();
  }

  /** Snap-helper die in de huidige modus probeert te magneetbinden aan een
   * bestaand vertex. */
  function snapForCurrentDraw(e: maplibregl.MapMouseEvent): [number, number] | null {
    const map = mapRef.current!;
    const { scenario } = useStore.getState();
    return findSnapVertex(
      scenario.blocks,
      e.point,
      (ll) => map.project(ll),
      SNAP_PIXELS,
    );
  }

  function findBlockAt(lngLat: [number, number]): Block | null {
    const { scenario } = useStore.getState();
    // We doorlopen van laatst-getekend naar eerst-getekend, zodat bovenliggende
    // blokken (laatst toegevoegd) eerst worden geraakt.
    for (let i = scenario.blocks.length - 1; i >= 0; i--) {
      if (pointInPolygon(lngLat, scenario.blocks[i].polygon)) return scenario.blocks[i];
    }
    return null;
  }

  return <div ref={containerRef} className="w-full h-full" />;
}

// Module-level handle naar de actieve kaart-instantie zodat panelen (zoals
// de bloklijst) "centreer in beeld" kunnen aanroepen zonder via context te
// hoeven gaan. Wordt gezet in de mount-effect en weer op null bij unmount.
let mapInstance: MlMap | null = null;

export function getMapInstance(): MlMap | null {
  return mapInstance;
}

